import React, { useEffect, useState, useCallback, useRef } from 'react';
import { RefreshCw, Users, Phone, Trophy, Clock, Calendar, CreditCard, Film, Scissors, ArrowRight, FileText, XCircle, AlertTriangle, MessageCircle, Send, X, Mic, MicOff, Sparkles, User, Search, TrendingUp, LogOut } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { getToken, setToken, clearAuth } from '../lib/authFetch';
import CouplesAppStats from './CouplesAppStats';

const API = process.env.REACT_APP_BACKEND_URL;
const f = (v) => v == null ? '£0' : new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(v);
const fmtWeek = (d) => { try { return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); } catch { return d; } };

export default function OperatorDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [pw, setPw] = useState('');
  const [pwError, setPwError] = useState(false);

  // If already logged in (JWT in session), skip the password screen
  useEffect(() => { if (getToken()) setAuthed(true); }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pw }),
      });
      if (res.ok) {
        const { token } = await res.json();
        setToken(token);
        sessionStorage.setItem('wve_auth', 'true');
        setAuthed(true);
        setPwError(false);
      } else {
        setPwError(true);
      }
    } catch (err) {
      setPwError(true);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/operator-dashboard`);
      if (r.ok) setData(await r.json());
    } catch (e) { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  // Auto-refresh ticker: while the cache is updating, quietly re-fetch until it's fresh.
  useEffect(() => {
    if (data?.updating) {
      const t = setTimeout(() => load(), 8000);
      return () => clearTimeout(t);
    }
  }, [data?.updating, load]);

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0f0a1e 0%, #1a0e2e 40%, #0d0b1a 100%)' }}>
        <div className="rounded-3xl p-8 w-full max-w-sm border border-purple-500/20" style={{ background: 'rgba(20,12,40,0.8)', backdropFilter: 'blur(20px)' }}>
          <div className="text-center mb-6">
            <img src="/wve-logo.png" alt="WVE" className="w-16 h-16 rounded-full mx-auto mb-3 opacity-90" />
            <h1 className="text-xl font-bold text-white">Command Centre</h1>
            <p className="text-xs text-purple-300/50 mt-1">WVE Operator Dashboard</p>
          </div>
          <form onSubmit={handleLogin}>
            <input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="Password" data-testid="operator-password-input" className="w-full rounded-xl px-4 py-3 text-white text-sm mb-4 border border-purple-500/30 focus:outline-none focus:border-purple-400/60 placeholder-purple-300/30" style={{ background: 'rgba(30,18,60,0.6)' }} />
            {pwError && <p className="text-red-400 text-xs mb-3 text-center" data-testid="operator-password-error">Wrong password</p>}
            <button type="submit" data-testid="operator-login-btn" className="w-full rounded-xl py-3 font-semibold text-sm text-white transition" style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}>Access Dashboard</button>
          </form>
        </div>
      </div>
    );
  }

  const s = data?.stripe || {};
  const l = data?.leads || {};
  const q = data?.quotes || {};
  const r = data?.rota || {};
  const eq = data?.editingQueue || {};
  const pipe = data?.pipeline || {};
  const sources = l.sources || {};

  const cardBg = 'rgba(20,12,40,0.6)';
  const cardBorder = 'border border-purple-500/15';
  const glassStyle = { background: cardBg, backdropFilter: 'blur(12px)' };

  return (
    <div className="min-h-screen text-white" style={{ background: 'linear-gradient(135deg, #0f0a1e 0%, #1a0e2e 30%, #12082a 60%, #0d0b1a 100%)' }}>
      {/* Header */}
      <div className="px-6 py-5 border-b border-purple-500/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/wve-logo.png" alt="WVE" className="w-10 h-10 rounded-full" />
            <div>
              <h1 className="text-2xl font-bold">Welcome back, Alex</h1>
              <p className="text-sm text-purple-300/40">Here's your business at a glance · {data?.month || ''}{data?.updating ? <span className="text-amber-400/80" data-testid="operator-updating"> · updating…</span> : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={loading} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition border border-purple-500/20 ${loading ? 'opacity-50' : 'hover:border-purple-400/40'}`} style={glassStyle}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button onClick={() => { clearAuth(); setAuthed(false); }} data-testid="operator-logout-btn" title="Log out" className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition border border-purple-500/20 hover:border-purple-400/40" style={glassStyle}>
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-5">
        {/* Alerts */}
        {(data?.alerts || []).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {data.alerts.map((a, i) => (
              <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium border ${
                a.type === 'urgent' ? 'bg-red-500/10 border-red-500/30 text-red-400' :
                a.type === 'warning' ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' :
                'bg-purple-500/10 border-purple-500/30 text-purple-300'
              }`}>
                <AlertTriangle className="w-3.5 h-3.5" />
                {a.message}
              </div>
            ))}
          </div>
        )}

        {/* Row 1: Hero Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { label: 'Stripe MTD', value: f(s.mtdRevenue), sub: `${s.mtdPayments || 0} payments this month`, icon: CreditCard, gradient: 'from-emerald-500/20 to-emerald-900/10', iconColor: 'text-emerald-400', ring: 'ring-emerald-500/20' },
            { label: 'Leads', value: l.total || 0, sub: `${l.leadToCall || 0}% booked a call`, icon: Users, gradient: 'from-blue-500/20 to-blue-900/10', iconColor: 'text-blue-400', ring: 'ring-blue-500/20' },
            { label: 'Weddings This Month', value: r.weddingsThisMonth || 0, sub: `${r.pvThisMonth || 0} P&V · ${r.voThisMonth || 0} Video Only`, icon: Film, gradient: 'from-purple-500/20 to-purple-900/10', iconColor: 'text-purple-400', ring: 'ring-purple-500/20' },
            { label: 'Editing Queue', value: eq.total || 0, sub: 'Videos in production', icon: Scissors, gradient: 'from-amber-500/20 to-amber-900/10', iconColor: 'text-amber-400', ring: 'ring-amber-500/20' },
          ].map(card => (
            <div key={card.label} className={`rounded-2xl p-5 ${cardBorder} ring-1 ${card.ring} bg-gradient-to-br ${card.gradient}`} style={{ backdropFilter: 'blur(12px)' }}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`p-2 rounded-xl bg-white/5 ${card.iconColor}`}><card.icon className="w-5 h-5" /></div>
              </div>
              <p className="text-3xl font-bold">{card.value}</p>
              <p className="text-xs text-purple-200/40 mt-1">{card.label}</p>
              <p className="text-[11px] text-purple-300/30 mt-0.5">{card.sub}</p>
            </div>
          ))}
          {/* Cash Position — auto from latest Pulse reading (read-only) */}
          <div className={`rounded-2xl p-5 ${cardBorder} ring-1 ring-cyan-500/20 bg-gradient-to-br from-cyan-500/20 to-cyan-900/10`} style={{ backdropFilter: 'blur(12px)' }} data-testid="cash-position-card">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 rounded-xl bg-white/5 text-cyan-400"><CreditCard className="w-5 h-5" /></div>
            </div>
            <p className="text-3xl font-bold" data-testid="cash-position-value">{f(data?.cashPosition || 0)}</p>
            <p className="text-xs text-purple-200/40 mt-1">Cash Position</p>
            <p className="text-[11px] text-purple-300/30 mt-0.5" data-testid="cash-position-asof">
              {data?.cashPositionAsOf ? `from Pulse — w/c ${fmtWeek(data.cashPositionAsOf)}` : 'no Pulse reading yet'}
            </p>
          </div>
        </div>

        {/* Row 2: Funnel + Quotes */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Sales Funnel */}
          <div className={`lg:col-span-3 rounded-2xl p-6 ${cardBorder}`} style={glassStyle}>
            <h3 className="text-sm font-semibold text-purple-200/50 uppercase tracking-wider mb-5">Sales Funnel</h3>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              {[
                { v: l.total || 0, label: 'Leads', color: 'text-white' },
                { arrow: true, pct: `${l.leadToCall || 0}%` },
                { v: l.hadCall || 0, label: 'Had Call', color: 'text-white' },
                { arrow: true, pct: `${l.callToWon || 0}%` },
                { v: l.won || 0, label: 'Won', color: 'text-emerald-400' },
                { sep: true },
                { v: l.pending || 0, label: 'Pending', color: 'text-amber-400' },
                { v: l.lost || 0, label: 'Lost', color: 'text-red-400' },
                { v: l.noCallYet || 0, label: 'No Call', color: 'text-purple-300/30' },
              ].map((item, i) => {
                if (item.arrow) return <div key={i} className="flex flex-col items-center"><ArrowRight className="w-4 h-4 text-purple-500/40" /><span className="text-[10px] text-purple-400/60">{item.pct}</span></div>;
                if (item.sep) return <div key={i} className="border-l border-purple-500/20 h-12 mx-1" />;
                return <div key={i} className="text-center"><p className={`text-2xl font-bold ${item.color}`}>{item.v}</p><p className="text-[10px] text-purple-300/40">{item.label}</p></div>;
              })}
            </div>
            <div className="mt-5 rounded-xl p-4 flex items-center justify-between border border-purple-500/10" style={{ background: 'rgba(16,10,30,0.5)' }}>
              <div className="flex items-center gap-8">
                <div data-testid="won-revenue-block">
                  <p className="text-[10px] text-purple-300/40 uppercase">Won Revenue</p>
                  <p className="text-2xl font-bold text-emerald-400">{f(l.wonRevenue)}</p>
                </div>
                <div data-testid="deposits-collected-block">
                  <p className="text-[10px] text-purple-300/40 uppercase">Deposits Collected</p>
                  <p className="text-2xl font-bold text-cyan-300" data-testid="deposits-collected-value">{f(q.depositsCollected || 0)}</p>
                  <p className="text-[9px] text-purple-300/30">{q.depositsCount || 0} paid this month</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-purple-300/40 uppercase mb-1">Recent Wins</p>
                {(l.recentWon || []).slice(0, 3).map((w, i) => (
                  <p key={i} className="text-[11px] text-purple-200/50">{w.name} — {f(w.amount)}</p>
                ))}
              </div>
            </div>
          </div>

          {/* Quotes */}
          <div className={`lg:col-span-2 rounded-2xl p-6 ${cardBorder}`} style={glassStyle}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-semibold text-purple-200/50 uppercase tracking-wider">Quotes</h3>
              <div className="text-right">
                <p className="text-lg font-bold text-purple-300">{q.conversionRate || 0}%</p>
                <p className="text-[9px] text-purple-300/30">Sent→Accepted ({q.totalAcceptedAllTime || 0}/{q.totalSentAllTime || 0})</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl p-4 border border-amber-500/20" style={{ background: 'rgba(16,10,30,0.5)' }}>
                <FileText className="w-4 h-4 text-amber-400 mb-2" />
                <p className="text-2xl font-bold text-amber-400">{q.activeSent || 0}</p>
                <p className="text-[10px] text-purple-300/40">Active Sent</p>
                <p className="text-xs text-purple-200/30 mt-0.5">{f(q.activeValue)} pipeline</p>
              </div>
              <div className="rounded-xl p-4 border border-emerald-500/20" style={{ background: 'rgba(16,10,30,0.5)' }}>
                <Trophy className="w-4 h-4 text-emerald-400 mb-2" />
                <p className="text-2xl font-bold text-emerald-400">{q.acceptedTotal || 0}</p>
                <p className="text-[10px] text-purple-300/40">Accepted This Month</p>
                <p className="text-xs text-purple-200/30 mt-0.5">{f(q.acceptedValue)}</p>
              </div>
              <div className="rounded-xl p-4 border border-red-500/20" style={{ background: 'rgba(16,10,30,0.5)' }}>
                <AlertTriangle className="w-4 h-4 text-red-400 mb-2" />
                <p className="text-2xl font-bold text-red-400">{q.expired || 0}</p>
                <p className="text-[10px] text-purple-300/40">Expired</p>
              </div>
              <div className="rounded-xl p-4 border border-purple-500/20" style={{ background: 'rgba(16,10,30,0.5)' }}>
                <XCircle className="w-4 h-4 text-purple-400/60 mb-2" />
                <p className="text-2xl font-bold text-purple-300/40">{q.declinedTotal || 0}</p>
                <p className="text-[10px] text-purple-300/40">Declined</p>
              </div>
            </div>
          </div>
        </div>

        {/* Row 3: Sources + Pipeline + Upcoming */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Sources */}
          <div className={`rounded-2xl p-6 ${cardBorder}`} style={glassStyle}>
            <h3 className="text-sm font-semibold text-purple-200/50 uppercase tracking-wider mb-4">Lead Sources</h3>
            <div className="space-y-3">
              {Object.entries(sources).slice(0, 7).map(([src, count]) => {
                const pct = l.total ? Math.round(count / l.total * 100) : 0;
                return (
                  <div key={src}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-purple-100/70">{src}</span>
                      <span className="text-purple-300/40">{count} ({pct}%)</span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(100,60,180,0.15)' }}>
                      <div className="h-full rounded-full" style={{ width: `${Math.max(pct, 2)}%`, background: 'linear-gradient(90deg, #7c3aed, #a855f7, #c084fc)' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pipeline */}
          <div className={`rounded-2xl p-6 ${cardBorder}`} style={glassStyle}>
            <h3 className="text-sm font-semibold text-purple-200/50 uppercase tracking-wider mb-4">Active Pipeline</h3>
            <div className="space-y-3">
              {Object.entries(pipe).map(([stage, count]) => (
                <div key={stage} className="flex items-center justify-between rounded-xl p-3 border border-purple-500/10" style={{ background: 'rgba(16,10,30,0.4)' }}>
                  <span className="text-sm text-purple-100/60">{stage}</span>
                  <span className="text-lg font-bold">{count}</span>
                </div>
              ))}
              <div className="flex items-center justify-between rounded-xl p-3 border border-purple-400/30" style={{ background: 'rgba(124,58,237,0.1)' }}>
                <span className="text-sm font-medium text-purple-200/80">Total Active</span>
                <span className="text-xl font-bold text-purple-300">{Object.values(pipe).reduce((a, b) => a + b, 0)}</span>
              </div>
            </div>
          </div>

          {/* Upcoming */}
          <div className={`rounded-2xl p-6 ${cardBorder}`} style={glassStyle}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-purple-200/50 uppercase tracking-wider">Upcoming Weddings</h3>
              <span className="text-xs font-medium text-amber-400">{r.weddingsThisWeek || 0} this week</span>
            </div>
            <div className="space-y-2">
              {(r.upcoming || []).map((j, i) => (
                <div key={i} className="rounded-xl p-3 border border-purple-500/10" style={{ background: 'rgba(16,10,30,0.4)' }}>
                  <p className="text-xs font-medium text-white truncate">{j.name}</p>
                  <div className="flex items-center gap-2 text-[10px] text-purple-300/40 mt-1">
                    <Calendar className="w-3 h-3" /> {j.date}
                    <span className="truncate">{(j.location || '').substring(0, 22)}</span>
                    {j.booth && <span className="text-amber-400 font-medium">+Booth</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Row 4: Yearly Weddings + Editing */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Yearly Weddings */}
          <div className={`rounded-2xl p-6 ${cardBorder}`} style={glassStyle}>
            <h3 className="text-sm font-semibold text-purple-200/50 uppercase tracking-wider mb-4">Weddings by Year</h3>
            <div className="space-y-3">
              {Object.entries(r.yearly || {}).sort(([a],[b]) => a.localeCompare(b)).map(([year, counts]) => (
                <div key={year} className="rounded-xl p-4 border border-purple-500/10 flex items-center justify-between" style={{ background: 'rgba(16,10,30,0.4)' }}>
                  <div>
                    <p className="text-lg font-bold text-white">{year}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-[11px] text-purple-400">P&V: {counts.photoVideo}</span>
                      <span className="text-[11px] text-blue-400">Video: {counts.videoOnly}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-white">{counts.total}</p>
                    <p className="text-[10px] text-purple-300/40">total</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Editing */}
          <div className={`rounded-2xl p-6 ${cardBorder}`} style={glassStyle}>
            <h3 className="text-sm font-semibold text-purple-200/50 uppercase tracking-wider mb-4">Editing Queue — Next Up</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(eq.active || []).map((q, i) => (
                <div key={i} className="rounded-xl p-3 border border-purple-500/10" style={{ background: 'rgba(16,10,30,0.4)' }}>
                  <p className="text-sm font-medium text-white truncate">{q.couple}</p>
                  <div className="flex items-center gap-1 text-[10px] text-purple-300/40 mt-1">
                    <Clock className="w-3 h-3" /> {q.finish ? `Due: ${q.finish}` : 'TBC'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* AI & Search Visibility */}
        <VisibilitySection />

        {/* WVE Couples App stats */}
        <CouplesAppStats />

        <p className="text-center text-[10px] text-purple-500/20 pb-6">WVE Command Centre · {data?.month}</p>
      </div>

      {/* AI Chat Bubble */}
      <AiChat dashboardData={data} />
    </div>
  );
}

function VisibilitySection() {
  const [vis, setVis] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [gsc, setGsc] = useState(null);
  const [ga, setGa] = useState(null);
  const [report, setReport] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/seo/search-console`).then(r => r.ok ? r.json() : null).then(setGsc).catch(() => {});
    fetch(`${API}/api/seo/analytics`).then(r => r.ok ? r.json() : null).then(setGa).catch(() => {});
    fetch(`${API}/api/reports/weekly/status`).then(r => r.ok ? r.json() : null).then(setReport).catch(() => {});
  }, []);

  const sendTestEmail = async () => {
    setSendingTest(true); setTestResult(null);
    try {
      const r = await fetch(`${API}/api/reports/weekly/send-test`, { method: 'POST' });
      const d = await r.json();
      if (d.sent) setTestResult({ ok: true, msg: `Sent to ${(d.results || []).map(x => x.to).join(', ')}` });
      else setTestResult({ ok: false, msg: d.error || 'Failed to send' });
    } catch (e) {
      setTestResult({ ok: false, msg: 'Request failed' });
    }
    setSendingTest(false);
  };

  const loadVis = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/seo/llm-visibility`);
      if (r.ok) {
        const d = await r.json();
        setVis(d);
        setRefreshing(!!d.running);
        return d;
      }
    } catch (e) { /* */ }
    return null;
  }, []);

  useEffect(() => {
    loadVis();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadVis]);

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const d = await loadVis();
      if (d && !d.running) { clearInterval(pollRef.current); pollRef.current = null; setRefreshing(false); }
    }, 5000);
  }, [loadVis]);

  const refresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await fetch(`${API}/api/seo/llm-visibility/refresh`, { method: 'POST' });
      startPolling();
    } catch (e) { setRefreshing(false); }
  };

  const latest = vis?.latest;
  const models = latest?.models || {};
  const MODEL_LABELS = { claude: 'Claude', openai: 'ChatGPT', gemini: 'Gemini' };
  const MODEL_COLORS = { claude: 'text-orange-300', openai: 'text-emerald-300', gemini: 'text-blue-300' };
  const scoreColor = (s) => s >= 50 ? 'text-emerald-400' : s >= 20 ? 'text-amber-400' : 'text-red-400';
  const cardBorder = 'border border-purple-500/15';
  const glassStyle = { background: 'rgba(20,12,40,0.6)', backdropFilter: 'blur(12px)' };

  return (
    <div className={`rounded-2xl p-6 ${cardBorder}`} style={glassStyle} data-testid="visibility-section">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-fuchsia-500/10 text-fuchsia-300"><Sparkles className="w-4 h-4" /></div>
          <h3 className="text-sm font-semibold text-purple-200/60 uppercase tracking-wider">AI &amp; Search Visibility</h3>
        </div>
        <div className="flex items-center gap-3">
          {latest?.updatedAt && (
            <span className="text-[10px] text-purple-300/30">Updated {new Date(latest.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
          )}
          <button onClick={refresh} disabled={refreshing} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition border border-purple-500/20 ${refreshing ? 'opacity-50' : 'hover:border-purple-400/40'}`} style={glassStyle} data-testid="visibility-refresh-btn">
            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} /> {refreshing ? 'Checking…' : 'Refresh'}
          </button>
        </div>
      </div>
      <p className="text-[11px] text-purple-300/30 mb-5">Do Claude, ChatGPT &amp; Gemini recommend Wedding Video Essex when couples ask for videographers?</p>

      {vis?.lastStatus === 'search_unavailable' && (
        <div className="rounded-lg px-3 py-2 mb-4 text-[11px] border border-amber-500/25 bg-amber-500/10 text-amber-300" data-testid="search-unavailable-note">
          Live web search is temporarily unavailable (search plan not active) — showing your last successful reading. Activate the Brave free plan to refresh.
        </div>
      )}

      <div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
      <div data-testid="ai-visibility-column">
      {!latest ? (
        <div className="text-center py-8 text-purple-300/40 text-sm">
          {refreshing ? 'Running first visibility check across Claude, ChatGPT & Gemini…' : 'No data yet — hit Refresh to run the first check.'}
        </div>
      ) : (
        <>
          {/* Score row */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="rounded-xl p-4 border border-fuchsia-500/20 bg-gradient-to-br from-fuchsia-500/15 to-purple-900/10" data-testid="visibility-overall">
              <p className={`text-4xl font-bold ${scoreColor(latest.overall)}`}>{latest.overall}%</p>
              <p className="text-[11px] text-purple-200/50 mt-1">Overall AI Visibility</p>
              <p className="text-[10px] text-purple-300/30 mt-0.5">Avg mention rate across models</p>
            </div>
            {['claude', 'openai', 'gemini'].map(mk => {
              const m = models[mk] || {};
              return (
                <div key={mk} className="rounded-xl p-4 border border-purple-500/10" style={{ background: 'rgba(16,10,30,0.4)' }} data-testid={`visibility-model-${mk}`}>
                  <p className={`text-3xl font-bold ${scoreColor(m.score || 0)}`}>{m.score || 0}%</p>
                  <p className={`text-[11px] mt-1 font-medium ${MODEL_COLORS[mk]}`}>{MODEL_LABELS[mk]}</p>
                  <p className="text-[10px] text-purple-300/30 mt-0.5">
                    {m.errors === m.total && m.total > 0 ? 'Unavailable' : `Mentioned in ${m.mentioned || 0}/${m.total || 0}`}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Insight banner */}
          <div className={`rounded-xl px-4 py-3 mb-4 text-xs border ${latest.overall === 0 ? 'bg-red-500/10 border-red-500/25 text-red-300' : latest.overall >= 50 ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300' : 'bg-amber-500/10 border-amber-500/25 text-amber-300'}`}>
            {latest.overall === 0
              ? 'No AI assistant currently recommends WVE for Essex wedding videography — a big GEO opportunity. Build citations, reviews & structured content to get named.'
              : latest.overall >= 50
                ? 'Strong AI visibility — WVE is being recommended in most buyer queries. Keep it up.'
                : 'WVE shows up in some AI answers but not consistently. More authoritative mentions & reviews will lift this.'}
          </div>

          {/* Trend chart */}
          {(() => {
            const trend = (vis?.history || []).map(h => ({
              date: new Date(h.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
              Overall: h.overall,
              Claude: h.models?.claude?.score ?? null,
              ChatGPT: h.models?.openai?.score ?? null,
              Gemini: h.models?.gemini?.score ?? null,
            }));
            if (trend.length < 2) return (
              <p className="text-[10px] text-purple-300/25 mb-4" data-testid="visibility-trend-hint">Trend chart appears once there are 2+ daily readings — it updates automatically each day.</p>
            );
            return (
              <div className="rounded-xl p-4 mb-4 border border-purple-500/10" style={{ background: 'rgba(16,10,30,0.4)' }} data-testid="visibility-trend-chart">
                <p className="text-[10px] text-purple-300/40 uppercase tracking-wider mb-2">AI visibility over time (%)</p>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(168,139,250,0.1)" />
                    <XAxis dataKey="date" stroke="#a78bfa66" tick={{ fontSize: 10 }} />
                    <YAxis domain={[0, 100]} stroke="#a78bfa66" tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: '#1a1030', border: '1px solid rgba(168,139,250,0.3)', borderRadius: 8, fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Line type="monotone" dataKey="Overall" stroke="#e879f9" strokeWidth={3} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="Claude" stroke="#fdba74" strokeWidth={1.5} dot={{ r: 2 }} />
                    <Line type="monotone" dataKey="ChatGPT" stroke="#6ee7b7" strokeWidth={1.5} dot={{ r: 2 }} />
                    <Line type="monotone" dataKey="Gemini" stroke="#93c5fd" strokeWidth={1.5} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            );
          })()}

          {/* Per-question breakdown */}
          <button onClick={() => setExpanded(!expanded)} className="text-xs text-purple-300/50 hover:text-purple-200 transition flex items-center gap-1" data-testid="visibility-toggle-details">
            {expanded ? 'Hide' : 'Show'} question-by-question breakdown ({(latest.questions || []).length} buyer questions)
            <ArrowRight className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </button>
          {expanded && (
            <div className="mt-3 space-y-2">
              {(latest.questions || []).map((q, i) => (
                <div key={i} className="rounded-xl p-3 border border-purple-500/10" style={{ background: 'rgba(16,10,30,0.4)' }}>
                  <p className="text-xs text-purple-100/70 mb-2">“{q.question}”</p>
                  <div className="flex flex-wrap gap-2">
                    {['claude', 'openai', 'gemini'].map(mk => {
                      const r = q.results?.[mk] || {};
                      const err = r.error;
                      return (
                        <span key={mk} className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${err ? 'bg-stone-500/10 border-stone-500/20 text-stone-400' : r.mentioned ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-red-500/5 border-red-500/20 text-red-300/70'}`}>
                          {err ? '—' : r.mentioned ? '✓' : '✗'} {MODEL_LABELS[mk]}
                        </span>
                      );
                    })}
                  </div>
                  {(q.sources || []).length > 0 && (
                    <p className="text-[9px] text-purple-300/25 mt-2">Grounded on {q.sources.length} live web results incl. {q.sources.slice(0, 2).join(', ')}…</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
      </div>

      {/* Google Analytics (GA4) */}
      <div data-testid="ga4-section">
        <div className="flex items-center gap-2 mb-1">
          <div className="p-2 rounded-xl bg-orange-500/10 text-orange-300"><Users className="w-4 h-4" /></div>
          <h4 className="text-sm font-semibold text-purple-200/60 uppercase tracking-wider">Google Analytics (Website Traffic)</h4>
        </div>
        <p className="text-[11px] text-purple-300/30 mb-4">Live GA4 traffic · last 28 days vs previous 28</p>

        {!ga ? (
          <div className="text-center py-6 text-purple-300/40 text-sm">Loading traffic data…</div>
        ) : ga.configured === false ? (
          <div className="rounded-xl px-4 py-3 border border-amber-500/25 bg-amber-500/10 text-amber-300 text-xs">Not configured.</div>
        ) : (
          <div className="space-y-4">
            {(ga.properties || []).map((p) => (
              <div key={p.propertyId} className="rounded-xl p-4 border border-purple-500/10" style={{ background: 'rgba(16,10,30,0.4)' }} data-testid={`ga4-prop-${p.propertyId}`}>
                <p className="text-xs font-medium text-purple-100/70 mb-3">{p.name || `Property ${p.propertyId}`}</p>
                {p.error ? (
                  <p className="text-[11px] text-amber-300/80">{p.error}</p>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <GscStat label="Sessions" value={p.current.sessions} delta={p.deltas.sessions} />
                      <GscStat label="Users" value={p.current.users} delta={p.deltas.users} />
                      <GscStat label="Page Views" value={p.current.views} delta={p.deltas.views} />
                    </div>
                    {(p.topPages || []).length > 0 && (
                      <div>
                        <p className="text-[10px] text-purple-300/30 uppercase tracking-wider mb-1.5">Top pages</p>
                        <div className="space-y-1">
                          {p.topPages.slice(0, 6).map((pg, i) => (
                            <div key={i} className="flex items-center justify-between text-[11px] text-purple-100/60 gap-2">
                              <span className="truncate">{pg.path}</span>
                              <span className="text-purple-300/40 whitespace-nowrap">{pg.views} views</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      </div>

      {/* SEO / Search Console */}
      <div data-testid="gsc-section">
        <div className="flex items-center gap-2 mb-1">
          <div className="p-2 rounded-xl bg-blue-500/10 text-blue-300"><Search className="w-4 h-4" /></div>
          <h4 className="text-sm font-semibold text-purple-200/60 uppercase tracking-wider">Google Search Console (SEO)</h4>
        </div>
        <p className="text-[11px] text-purple-300/30 mb-4">Live Google search performance {gsc?.range ? `· ${gsc.range.start} → ${gsc.range.end}` : ''}</p>

        {!gsc ? (
          <div className="text-center py-6 text-purple-300/40 text-sm">Loading SEO data…</div>
        ) : gsc.configured === false ? (
          <div className="rounded-xl px-4 py-3 border border-amber-500/25 bg-amber-500/10 text-amber-300 text-xs" data-testid="gsc-placeholder">Not connected — add a Google service account to pull impressions, clicks & average position.</div>
        ) : gsc.error ? (
          <div className="rounded-xl px-4 py-3 border border-red-500/25 bg-red-500/10 text-red-300 text-xs">{gsc.error}</div>
        ) : (gsc.sites || []).length === 0 ? (
          <div className="rounded-xl px-4 py-3 border border-amber-500/25 bg-amber-500/10 text-amber-300 text-xs">Connected, but the service account has no Search Console properties yet. Add its email as a user to your properties.</div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-4">
            {gsc.sites.map((s) => (
              <div key={s.site} className="rounded-xl p-4 border border-purple-500/10" style={{ background: 'rgba(16,10,30,0.4)' }} data-testid={`gsc-site-${s.site.replace(/[^a-z0-9]/gi, '-')}`}>
                <p className="text-xs font-medium text-purple-100/70 mb-3">{s.site.replace(/^https?:\/\//, '').replace(/\/$/, '')}</p>
                {s.error ? (
                  <p className="text-[11px] text-red-300/70">{s.error}</p>
                ) : (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                      <GscStat label="Clicks" value={s.current.clicks} delta={s.deltas.clicks} />
                      <GscStat label="Impressions" value={s.current.impressions} delta={s.deltas.impressions} />
                      <GscStat label="Avg Position" value={s.current.position} delta={s.deltas.position} lowerBetter />
                      <GscStat label="CTR" value={`${s.current.ctr}%`} />
                    </div>
                    {s.current.clicks === 0 && s.current.impressions === 0 && (
                      <p className="text-[11px] text-purple-300/40 italic" data-testid="gsc-no-data">No Google search data for this period — this property likely redirects to your main domain.</p>
                    )}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-4">
                    {(s.topQueries || []).length > 0 && (
                      <div>
                        <p className="text-[10px] text-purple-300/30 uppercase tracking-wider mb-1.5">Top queries</p>
                        <div className="space-y-1">
                          {s.topQueries.slice(0, 6).map((q, i) => (
                            <div key={i} className="flex items-center justify-between text-[11px] text-purple-100/60 gap-2">
                              <span className="truncate">{q.query}</span>
                              <span className="text-purple-300/40 whitespace-nowrap">{q.clicks} clicks · pos {q.position}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {(s.quickWins || []).length > 0 && (
                      <div>
                        <div className="flex items-center gap-1.5 mb-2">
                          <TrendingUp className="w-3 h-3 text-emerald-300" />
                          <p className="text-[10px] text-emerald-300/70 uppercase tracking-wider">Quick SEO wins — page 1-2, one push to climb</p>
                        </div>
                        <div className="space-y-1.5">
                          {s.quickWins.map((w, i) => (
                            <div key={i} className="flex items-center justify-between gap-2 text-[11px]" data-testid={`quick-win-${i}`}>
                              <span className="truncate text-purple-100/70">{w.query}</span>
                              <span className="flex items-center gap-2 whitespace-nowrap">
                                <span className="text-purple-300/40">{w.impressions.toLocaleString()} imp</span>
                                <span className={`font-semibold px-1.5 py-0.5 rounded ${w.position <= 10 ? 'bg-amber-500/15 text-amber-300' : 'bg-orange-500/10 text-orange-300/80'}`}>pos {w.position}</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    </div>
                  </>
                )}
              </div>
            ))}
            </div>
            <p className="text-[10px] text-purple-300/25">28-day window vs the previous 28 days. Google data lags ~3 days.</p>
            {(gsc.history || []).length >= 2 && (
              <div className="rounded-xl p-4 border border-purple-500/10" style={{ background: 'rgba(16,10,30,0.4)' }} data-testid="gsc-trend-chart">
                <p className="text-[10px] text-purple-300/40 uppercase tracking-wider mb-2">Search clicks over time (all properties)</p>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={(gsc.history || []).map(h => ({ date: new Date(h.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }), Clicks: h.clicks }))} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(168,139,250,0.1)" />
                    <XAxis dataKey="date" stroke="#a78bfa66" tick={{ fontSize: 10 }} />
                    <YAxis stroke="#a78bfa66" tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: '#1a1030', border: '1px solid rgba(168,139,250,0.3)', borderRadius: 8, fontSize: 11 }} />
                    <Line type="monotone" dataKey="Clicks" stroke="#60a5fa" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

function GscStat({ label, value, delta, lowerBetter }) {
  const hasDelta = delta !== undefined && delta !== null && delta !== 0;
  const good = lowerBetter ? delta < 0 : delta > 0;
  return (
    <div className="rounded-lg p-3 border border-purple-500/10" style={{ background: 'rgba(20,12,40,0.5)' }}>
      <p className="text-xl font-bold text-purple-50">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      <div className="flex items-center gap-1.5 mt-0.5">
        <p className="text-[10px] text-purple-300/40">{label}</p>
        {hasDelta && (
          <span className={`text-[10px] font-semibold ${good ? 'text-emerald-400' : 'text-red-400'}`}>
            {delta > 0 ? '+' : ''}{typeof delta === 'number' ? delta.toLocaleString() : delta}
          </span>
        )}
      </div>
    </div>
  );
}

function AiChat({ dashboardData }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [sessionId] = useState(() => `cmd-${Date.now()}`);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/insights/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, sessionId }),
      });
      if (res.ok) {
        const d = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: d.response }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Failed to get response.' }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection error.' }]);
    }
    setLoading(false);
    inputRef.current?.focus();
  };

  const toggleVoice = () => {
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const recognition = new SR();
    recognition.lang = 'en-GB';
    recognition.interimResults = false;
    recognition.continuous = false;
    recognition.onresult = (e) => {
      const text = e.results[0][0].transcript;
      setInput(text);
      setListening(false);
      // Auto-send after voice input
      setTimeout(() => sendMessage(text), 200);
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const glassStyle = { background: 'rgba(20,12,40,0.95)', backdropFilter: 'blur(20px)' };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl z-50 transition-transform hover:scale-110 border border-purple-500/30"
        style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
        data-testid="ai-chat-bubble"
      >
        <Sparkles className="w-6 h-6 text-white" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-[400px] h-[550px] rounded-2xl border border-purple-500/20 shadow-2xl z-50 flex flex-col overflow-hidden" style={glassStyle} data-testid="ai-chat-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-purple-500/15">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-400" />
          <div>
            <p className="text-sm font-semibold text-white">AI Assistant</p>
            <p className="text-[10px] text-purple-300/40">Ask anything about your business</p>
          </div>
        </div>
        <button onClick={() => setOpen(false)} className="p-1 rounded-lg hover:bg-white/5 text-purple-300/50 hover:text-white transition">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Sparkles className="w-8 h-8 text-purple-500/30 mx-auto mb-3" />
            <p className="text-sm text-purple-200/40">Ask me anything</p>
            <div className="flex flex-wrap gap-1.5 mt-4 justify-center">
              {['How many leads this month?', 'What weddings do we have this week?', "What's our call to won rate?", 'Revenue this month vs last'].map(q => (
                <button key={q} onClick={() => sendMessage(q)} className="text-[10px] px-2 py-1 rounded-lg border border-purple-500/20 text-purple-300/50 hover:text-white hover:border-purple-400/40 transition">
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0 mt-1"><Sparkles className="w-3 h-3 text-purple-400" /></div>}
            <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${msg.role === 'user' ? 'text-white' : 'text-purple-100/80 border border-purple-500/15'}`} style={msg.role === 'user' ? { background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' } : { background: 'rgba(30,18,60,0.5)' }}>
              <div className="whitespace-pre-wrap leading-relaxed text-[13px]">{msg.content}</div>
            </div>
            {msg.role === 'user' && <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0 mt-1"><User className="w-3 h-3 text-purple-300" /></div>}
          </div>
        ))}
        {loading && (
          <div className="flex gap-2">
            <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0"><Sparkles className="w-3 h-3 text-purple-400 animate-pulse" /></div>
            <div className="rounded-xl px-3 py-2 border border-purple-500/15" style={{ background: 'rgba(30,18,60,0.5)' }}>
              <div className="flex gap-1"><div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} /><div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} /><div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} /></div>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div className="px-3 py-3 border-t border-purple-500/15">
        <form onSubmit={e => { e.preventDefault(); sendMessage(input); }} className="flex gap-2">
          <button type="button" onClick={toggleVoice} className={`p-2 rounded-xl transition border ${listening ? 'bg-red-500/20 border-red-500/40 text-red-400' : 'border-purple-500/20 text-purple-300/40 hover:text-white hover:border-purple-400/40'}`}>
            {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={listening ? 'Listening...' : 'Ask a question...'}
            disabled={loading || listening}
            className="flex-1 rounded-xl px-3 py-2 text-sm text-white placeholder-purple-300/30 border border-purple-500/20 focus:outline-none focus:border-purple-400/50"
            style={{ background: 'rgba(30,18,60,0.4)' }}
          />
          <button type="submit" disabled={loading || !input.trim()} className="p-2 rounded-xl transition border border-purple-500/20 text-purple-300/40 hover:text-white disabled:opacity-30" style={{ background: input.trim() ? 'linear-gradient(135deg, #7c3aed, #6d28d9)' : 'transparent' }}>
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}

